import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ActionMenu } from './ActionMenu';
import { NavigationIcon } from './NavigationIcon';
import { WhatsNew } from './WhatsNew';
import './QuickCreate.css';
import type { AppRoute } from '../routes';
import type { ContextCreateKind } from '../contextual-create';

const navigation = [
  { label: 'Overview', href: '#overview' },
  { label: 'Organisations', href: '#organisations' },
  { label: 'Pipeline', href: '#pipeline' },
  { label: 'Reports', href: '#reports' },
  { label: 'Opportunities', href: '#opportunities' },
  { label: 'Tasks', href: '#tasks' },
  { label: 'Catalogue', href: '#catalogue' },
  { label: 'Team', href: '#team' },
  { label: 'Onboarding', href: '#onboarding' },
  { label: 'Xero', href: '#xero' },
  { label: 'Migration', href: '#migration' },
  { label: 'Audit', href: '#audit' },
  { label: 'Administration', href: '#administration' },
] as const;

interface AppShellProps {
  search?: ReactNode;
  children: ReactNode;
  onSignIn: () => Promise<void>;
  onSignOut?: () => Promise<void>;
  authenticationStatus?: 'checking' | 'signed-in' | 'signed-out' | 'error';
  activeRoute?: AppRoute;
  visibleRoutes?: readonly AppRoute[];
  onRouteIntent?: ((route: AppRoute) => void) | undefined;
  accountEmail?: string | undefined;
  accountRoleLabels?: readonly string[] | undefined;
  createRoutes?: readonly ('organisations' | 'opportunities')[];
  onCreateChild?: ((kind: ContextCreateKind, trigger: HTMLElement) => void) | undefined;
  profitabilityAvailable?: boolean;
  documentsAvailable?: boolean;
}

export function AppShell({
  search,
  children,
  onSignIn,
  onSignOut,
  authenticationStatus = 'signed-out',
  activeRoute = 'overview',
  visibleRoutes,
  onRouteIntent,
  accountEmail,
  accountRoleLabels,
  createRoutes = [],
  onCreateChild,
  profitabilityAvailable = false,
  documentsAvailable = false,
}: AppShellProps) {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [narrow, setNarrow] = useState(
    () => window.matchMedia?.('(max-width: 62rem)').matches ?? false,
  );
  const main = useRef<HTMLElement>(null);
  const previousRoute = useRef(activeRoute);
  const menu = useRef<HTMLButtonElement>(null);
  const drawer = useRef<HTMLDialogElement>(null);
  const header = useRef<HTMLElement>(null);
  const shell = useRef<HTMLDivElement>(null);
  const [signInError, setSignInError] = useState<string>();
  // Offer shortcuts only from read views or fully covered source workflows.
  // Destination permissions alone cannot protect an unfinished child form.
  const updateLinksAvailable = ['overview', 'pipeline', 'audit', 'organisations', 'tasks'].includes(
    activeRoute,
  );

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 62rem)');
    const changed = () => {
      setNarrow(media?.matches ?? false);
      setNavigationOpen(false);
      if (drawer.current?.open) drawer.current.close();
    };
    media?.addEventListener('change', changed);
    return () => media?.removeEventListener('change', changed);
  }, []);
  useEffect(() => {
    if (!header.current || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      shell.current?.style.setProperty(
        '--app-header-height',
        `${header.current?.getBoundingClientRect().height ?? 80}px`,
      );
    });
    observer.observe(header.current);
    return () => observer.disconnect();
  }, [authenticationStatus]);
  useEffect(() => {
    if (previousRoute.current === activeRoute) return;
    previousRoute.current = activeRoute;
    if (authenticationStatus !== 'signed-in' || !main.current) return;
    const region = main.current;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    region.focus({ preventScroll: true });
    const focusHeading = () => {
      const heading = region.querySelector<HTMLElement>('h1, h2');
      if (!heading) return false;
      heading.tabIndex = -1;
      if (document.activeElement === region) heading.focus({ preventScroll: true });
      return true;
    };
    if (focusHeading()) return;
    const observer = new MutationObserver(() => {
      if (focusHeading()) observer.disconnect();
    });
    observer.observe(region, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [activeRoute, authenticationStatus]);
  function closeNavigation() {
    if (drawer.current?.open) drawer.current.close();
    setNavigationOpen(false);
    if (narrow) menu.current?.focus();
  }
  function links() {
    return (
      <nav
        id={narrow ? 'mobile-navigation' : 'primary-navigation'}
        className="navigation"
        aria-label="Primary navigation"
      >
        {[
          {
            title: 'Workspace',
            routes: ['overview', 'organisations', 'pipeline', 'opportunities', 'tasks', 'reports'],
          },
          { title: 'Resources', routes: ['catalogue', 'team', 'onboarding'] },
          { title: 'Settings & tools', routes: ['xero', 'migration', 'audit', 'administration'] },
        ]
          .filter((group) =>
            group.routes.some(
              (route) => !visibleRoutes || visibleRoutes.includes(route as AppRoute),
            ),
          )
          .map((group) => (
            <div className="navigation-group" key={group.title}>
              <p>{group.title}</p>
              {group.routes
                .filter((route) => !visibleRoutes || visibleRoutes.includes(route as AppRoute))
                .map((route) => navigation.find((item) => item.href === `#${route}`)!)
                .map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    aria-current={item.href === `#${activeRoute}` ? 'page' : undefined}
                    onClick={closeNavigation}
                    onPointerEnter={() => onRouteIntent?.(item.href.slice(1) as AppRoute)}
                    onFocus={() => onRouteIntent?.(item.href.slice(1) as AppRoute)}
                  >
                    <NavigationIcon route={item.href.slice(1) as AppRoute} />
                    {item.label}
                  </a>
                ))}
            </div>
          ))}
      </nav>
    );
  }

  async function handleSignIn() {
    try {
      setSignInError(undefined);
      await onSignIn();
    } catch {
      setSignInError(
        'Microsoft sign-in could not start. Try again, or contact your CRM administrator.',
      );
    }
  }

  async function handleAuthenticationAction() {
    if (authenticationStatus === 'signed-in' && onSignOut) {
      await onSignOut();
      return;
    }
    await handleSignIn();
  }

  if (authenticationStatus !== 'signed-in') {
    return (
      <div ref={shell} className="auth-shell">
        <header ref={header} className="auth-topbar">
          <a className="brand" href="#overview" aria-label="Resilience CRM home">
            <span className="brand-mark" aria-hidden="true" />
            <span>
              <strong>Resilience</strong>
              <small>CRM</small>
            </span>
          </a>
        </header>
        <main id="main-content" className="auth-main" tabIndex={-1}>
          <div className="auth-panel">
            {children}
            {authenticationStatus === 'error' ? (
              <div className="banner" role="alert">
                Microsoft sign-in could not be verified. No session was retained.
              </div>
            ) : null}
            {signInError ? (
              <div className="banner" role="alert">
                {signInError}
              </div>
            ) : null}
            <button
              className="button auth-sign-in"
              type="button"
              disabled={authenticationStatus === 'checking'}
              onClick={() => void handleSignIn()}
            >
              {authenticationStatus === 'checking'
                ? 'Completing sign-in…'
                : 'Sign in with Microsoft'}
              <span aria-hidden="true">→</span>
            </button>
            <p className="auth-access-note">For authorised Resilience team members.</p>
          </div>
        </main>
        <footer className="auth-footer">
          <span>Resilience CRM</span>
          {import.meta.env.VITE_APP_ENV !== 'production' ? (
            <span>Synthetic preview · Demonstration data only</span>
          ) : null}
        </footer>
      </div>
    );
  }

  return (
    <div ref={shell} className={`app-shell crm-shell${collapsed ? ' crm-shell--collapsed' : ''}`}>
      <header ref={header} className="topbar">
        <a className="brand" href="#overview" aria-label="Resilience CRM home">
          <span className="brand-mark" aria-hidden="true" />
          <span>
            <strong>Resilience</strong>
            <small>CRM</small>
          </span>
        </a>
        <button
          ref={menu}
          className="menu-button"
          type="button"
          aria-controls={narrow ? 'navigation-drawer' : 'primary-navigation'}
          aria-expanded={narrow ? navigationOpen : !collapsed}
          onClick={() => {
            if (narrow) {
              setNavigationOpen(true);
              drawer.current?.showModal();
            } else setCollapsed((value) => !value);
          }}
        >
          <span className="sr-only">Toggle navigation</span>
          <span aria-hidden="true">☰</span>
        </button>
        {search ?? (
          <input
            className="search-placeholder"
            aria-label="Search CRM — sign in and activate CRM access to search"
            placeholder="Search CRM…"
            disabled
          />
        )}
        <div className="topbar-actions">
          {createRoutes.some((route) => !visibleRoutes || visibleRoutes.includes(route)) ? (
            <div className="quick-create">
              <ActionMenu
                label="New"
                showIndicator={false}
                icon={<span aria-hidden="true">＋</span>}
              >
                {createRoutes
                  .filter((route) => !visibleRoutes || visibleRoutes.includes(route))
                  .map((route) => (
                    <a
                      key={route}
                      href={`#${route}?create=1`}
                      onPointerEnter={() => onRouteIntent?.(route)}
                      onFocus={() => onRouteIntent?.(route)}
                      onClick={(event) => {
                        event.currentTarget.closest('details')?.removeAttribute('open');
                      }}
                    >
                      {route === 'organisations' ? 'Organisation' : 'Opportunity'}
                    </a>
                  ))}
                {onCreateChild
                  ? (['contact', 'task', 'activity'] as const)
                      .filter((kind) => {
                        const parentRoute = kind === 'contact' ? 'organisations' : 'opportunities';
                        return (
                          createRoutes.includes(parentRoute) &&
                          (!visibleRoutes || visibleRoutes.includes(parentRoute))
                        );
                      })
                      .map((kind) => (
                        <button
                          key={kind}
                          type="button"
                          onClick={(event) => {
                            const trigger = event.currentTarget
                              .closest('details')
                              ?.querySelector('summary');
                            if (trigger) onCreateChild(kind, trigger);
                          }}
                        >
                          {kind === 'contact' ? 'Contact' : kind === 'task' ? 'Task' : 'Activity'}
                        </button>
                      ))
                  : null}
              </ActionMenu>
            </div>
          ) : null}
          <WhatsNew
            profitabilityAvailable={profitabilityAvailable}
            documentsAvailable={documentsAvailable}
            visibleRoutes={
              updateLinksAvailable
                ? (visibleRoutes ?? navigation.map((item) => item.href.slice(1) as AppRoute))
                : []
            }
            onRouteIntent={onRouteIntent}
          />
          <ActionMenu
            label="Account"
            showIndicator={false}
            icon={
              accountEmail ? (
                <span className="account-avatar" aria-hidden="true">
                  {accountEmail.charAt(0).toLocaleUpperCase('en-ZA')}
                </span>
              ) : undefined
            }
          >
            {accountEmail ? (
              <div className="account-menu-identity">
                <span>Signed in as</span>
                <strong>{accountEmail}</strong>
                {accountRoleLabels?.length ? (
                  <span className="account-menu-roles">{accountRoleLabels.join(' · ')}</span>
                ) : null}
              </div>
            ) : null}
            <button
              className="button button--quiet"
              type="button"
              onClick={() => void handleAuthenticationAction()}
            >
              Sign out
            </button>
          </ActionMenu>
        </div>
      </header>
      <div className="app-body">
        {!narrow ? (
          <aside className="app-sidebar" hidden={collapsed}>
            {links()}
          </aside>
        ) : null}
        <div className="app-content">
          {signInError ? (
            <div className="banner" role="alert">
              {signInError}
            </div>
          ) : null}
          <main ref={main} id="main-content" tabIndex={-1}>
            {children}
          </main>
          <footer className="workspace-footer">
            <span>Resilience CRM</span>
            {import.meta.env.VITE_APP_ENV !== 'production' ? (
              <span>Synthetic preview · Demonstration data only</span>
            ) : null}
          </footer>
        </div>
      </div>
      <dialog
        ref={drawer}
        id="navigation-drawer"
        className="navigation-drawer"
        aria-label="CRM navigation"
        onCancel={(event) => {
          event.preventDefault();
          closeNavigation();
        }}
      >
        <button type="button" className="button button--secondary" onClick={closeNavigation}>
          Close navigation
        </button>
        {narrow ? links() : null}
      </dialog>
    </div>
  );
}
